import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useConfirm } from 'hooks/useConfirm';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { pureComponent } from 'lib/pureComponent';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  eventBarcodeSelected,
  OPEN_BARCODE_SCANNER,
  OPEN_BARCODE_VIEWER,
} from 'store/actions';

interface Props {
  value: string;
  onChange: (value: string) => void;
  showIconOnLeft?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const QRField: React.FC<Props> = pureComponent(
  ({ onChange, showIconOnLeft = false, style, value }) => {
    const confirm = useConfirm();
    const dispatch = useDispatch();

    const selectionKey = React.useRef(`selectionKeyQrField-${Math.random()}`);

    const handleDeselect = React.useCallback(
      () =>
        confirm({
          text: 'Do you want to delete the QR code data?',
          confirmText: 'Delete',
          destructive: true,
          onConfirm: () => onChange(''),
        }),
      [confirm, onChange],
    );

    const handleOpenQrScanner = React.useCallback(
      () =>
        dispatch(
          OPEN_BARCODE_SCANNER({
            selectionKey: selectionKey.current,
          }),
        ),
      [dispatch],
    );

    const handleOpenQrViewer = React.useCallback(
      () =>
        dispatch(
          OPEN_BARCODE_VIEWER({ data: value, deleteKey: selectionKey.current }),
        ),
      [dispatch, value],
    );

    React.useEffect(() => {
      const barcodeEvent = doAddEvent(eventBarcodeSelected, (action) => {
        if (action.payload.key === selectionKey.current) {
          onChange(action.payload.data);
        }
      });
      return () => {
        doRemoveEvent(barcodeEvent);
      };
    }, [onChange]);

    return (
      <CardRow
        label={value ? 'View QR Code' : 'Tap to Scan QR Code'}
        labelColor={value ? undefined : theme.colors.placeholder}
        icon={showIconOnLeft ? ICONS.QRCodeLight : undefined}
        navigationType={showIconOnLeft ? 'none' : 'qr'}
        actionIcon={ICONS.CircleXmarkLight}
        onAction={value ? handleDeselect : undefined}
        onPress={!value ? handleOpenQrScanner : handleOpenQrViewer}
        style={style}
      />
    );
  },
);
